import * as React from "react";

import {
  type FilterStageColorVars,
  stageColorStyle,
} from "../columnFilters/filterStageColors";
import type { HighlightRange } from "../search/highlightUtils";

import styles from "./HighlightedCell.module.css";

interface HighlightedCellProps {
  text: string;
  highlights: HighlightRange[];
  stageColors: FilterStageColorVars[];
}

function highlightsEqual(a: HighlightRange[], b: HighlightRange[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].start !== b[i].start ||
      a[i].end !== b[i].end ||
      a[i].colorIndex !== b[i].colorIndex
    ) {
      return false;
    }
  }
  return true;
}

export const HighlightedCell = React.memo(
  function HighlightedCell({
    text,
    highlights,
    stageColors,
  }: HighlightedCellProps) {
    if (highlights.length === 0) {
      return <>{text}</>;
    }

    const segments: React.ReactNode[] = [];
    let cursor = 0;

    for (const range of highlights) {
      if (range.start > cursor) {
        segments.push(
          <React.Fragment key={`text-${cursor}`}>
            {text.slice(cursor, range.start)}
          </React.Fragment>,
        );
      }
      segments.push(
        <mark
          key={`mark-${range.start}`}
          className={styles.highlight}
          style={stageColorStyle(range.colorIndex, stageColors)}
        >
          {text.slice(range.start, range.end)}
        </mark>,
      );
      cursor = range.end;
    }

    if (cursor < text.length) {
      segments.push(
        <React.Fragment key={`text-${cursor}`}>
          {text.slice(cursor)}
        </React.Fragment>,
      );
    }

    return <>{segments}</>;
  },
  (prev, next) =>
    prev.text === next.text &&
    prev.stageColors === next.stageColors &&
    highlightsEqual(prev.highlights, next.highlights),
);
